/**
 * product-compare.js
 * Handles comparison table building, sticky header, accordions,
 * differences toggle, share, and search.
 *
 * Supports two metafield structures:
 *
 * Structure A (legacy — plain arrays, plural key):
 *   { "technical_specifications": [...], "functions": [...], "activity": [...] }
 *
 * Structure B (new — object wrapper with optional title, singular key):
 *   { "technical_specification": { "title": "...", "data": [...] },
 *     "functions":               { "title": "...", "data": [...] },
 *     "activity":                { "title": "...", "data": [...] } }
 *
 * Both formats are normalised into plain arrays before any rendering.
 * The resolver checks Structure B first (object + .data array),
 * then falls back to Structure A (plain array).
 */

// ─────────────────────────────────────────────────────────────────────────────
// DUAL-STRUCTURE SECTION CONFIG
// Each entry maps one logical "tab" to its two possible metafield keys.
// ─────────────────────────────────────────────────────────────────────────────
const SECTION_CONFIG = [
  { keyA: 'technical_specifications', keyB: 'technical_specifications' },
  { keyA: 'functions',                keyB: 'functions'                },
  { keyA: 'activity',                 keyB: 'activity'                 }
];

/**
 * Resolve a section from a product spec object into a plain array.
 * Tries Structure B (object with .data) first, then Structure A (plain array).
 *
 * @param {Object} spec   - The full parsed metafield object for one product
 * @param {string} keyA   - Structure A key (e.g. 'technical_specifications')
 * @param {string} keyB   - Structure B key (e.g. 'technical_specification')
 * @returns {Array}       - Always returns an array (empty if nothing found)
 */
function resolveSection(spec, keyA, keyB) {
  if (!spec) return [];

  // Both structures use the same key — distinguish by value shape.
  // Structure B: object with a .data array → { title: '...', data: [...] }
  // Structure A: plain array → [...]
  const candidate = spec[keyB] || spec[keyA];

  if (!candidate) return [];

  // ── Structure B: object wrapper with .data array ──────────────────────────
  if (
    typeof candidate === 'object' &&
    !Array.isArray(candidate) &&
    Array.isArray(candidate.data)
  ) {
    return candidate.data;
  }

  // ── Structure A: plain array ──────────────────────────────────────────────
  if (Array.isArray(candidate)) {
    return candidate;
  }

  return [];
}

// ─────────────────────────────────────────────────────────────────────────────
// UTILITY HELPERS
// ─────────────────────────────────────────────────────────────────────────────

function replaceAndParseJSON(encodedString) {
  try {
    const decodedString = encodedString.replace(/&quot;/g, '"');
    const jsonData = JSON.parse(decodedString);
    return { success: true, decoded: decodedString, data: jsonData };
  } catch (error) {
    return { success: false, error: error.message };
  }
}

async function fetchTechnicalSpecification(handle) {
  const url = '/pages/get-product-specs?handle=' + handle;
  try {
    const response = await fetch(url);
    if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);

    const responseText = await response.text();
    let jsonData;

    if (responseText.includes('&quot;')) {
      const result = replaceAndParseJSON(responseText);
      if (result.success) {
        jsonData = result.data;
      } else {
        throw new Error(result.error);
      }
    } else {
      jsonData = JSON.parse(responseText);
    }

    console.log(jsonData);
    return jsonData;
  } catch (error) {
    console.error('Error fetching data: ', error);
    return null;
  }
}

/** Normalise a value for equality comparison */
function normalizeValue(value) {
  if (value === null || value === undefined) return null;
  return String(value).trim().toLowerCase();
}

/** Format a value for HTML display */
function formatValue(value) {
  if (value === null || value === undefined) {
    return '<span class="value-missing">-</span>';
  }
  const valueStr = String(value).trim();
  return escapeHtml(valueStr);
}

/** Safely escape HTML */
function escapeHtml(text) {
  const div = document.createElement('div');
  div.textContent = text;
  return div.innerHTML;
}

/** Format a price value */
function formatPrice(price) {
  if (!price) return '';
  if (typeof price === 'string') return price;
  return price.toFixed(2) + ' zł';
}

// ─────────────────────────────────────────────────────────────────────────────
// createCompleteComparison  (used for simple 2-product diff stats)
// ─────────────────────────────────────────────────────────────────────────────

function createCompleteComparison(product1, product2, name1, name2) {
  let totalFeatures = 0;
  let sameFeatures = 0;
  let differentFeatures = 0;
  let missingInProduct1 = 0;
  let missingInProduct2 = 0;
  let comparisonHTML = '';

  SECTION_CONFIG.forEach(({ keyA, keyB }) => {
    const sections1 = resolveSection(product1, keyA, keyB);
    const sections2 = resolveSection(product2, keyA, keyB);

    const allSectionTitles = new Set();
    sections1.forEach(s => allSectionTitles.add(s.title));
    sections2.forEach(s => allSectionTitles.add(s.title));

    Array.from(allSectionTitles).sort().forEach(sectionTitle => {
      const section1 = sections1.find(s => s.title === sectionTitle);
      const section2 = sections2.find(s => s.title === sectionTitle);

      const rows1 = section1 ? section1.rows : [];
      const rows2 = section2 ? section2.rows : [];

      const allLabels = new Set();
      rows1.forEach(r => allLabels.add(r.label));
      rows2.forEach(r => allLabels.add(r.label));

      if (allLabels.size === 0) return;

      comparisonHTML += `
        <div class="section">
          <div class="section-title">${escapeHtml(sectionTitle)}</div>
          <table class="comparison-table">
            <thead>
              <tr>
                <th>Feature</th>
                <th>${escapeHtml(name1)}</th>
                <th>${escapeHtml(name2)}</th>
              </tr>
            </thead>
            <tbody>
      `;

      Array.from(allLabels).sort().forEach(label => {
        const row1 = rows1.find(r => r.label === label);
        const row2 = rows2.find(r => r.label === label);

        const value1 = row1 ? row1.value : null;
        const value2 = row2 ? row2.value : null;

        totalFeatures++;

        let rowClass = '';
        if (value1 === null && value2 === null) {
          // both missing — no class
        } else if (value1 === null) {
          rowClass = 'difference'; missingInProduct1++; differentFeatures++;
        } else if (value2 === null) {
          rowClass = 'difference'; missingInProduct2++; differentFeatures++;
        } else if (normalizeValue(value1) === normalizeValue(value2)) {
          rowClass = 'same'; sameFeatures++;
        } else {
          rowClass = 'difference'; differentFeatures++;
        }

        const displayValue1 = value1 === null ? '<span class="value-missing">-</span>' : formatValue(value1);
        const displayValue2 = value2 === null ? '<span class="value-missing">-</span>' : formatValue(value2);
        const cellClass1 = value1 === null ? 'value-missing' : 'value-present';
        const cellClass2 = value2 === null ? 'value-missing' : 'value-present';

        comparisonHTML += `
          <tr class="${rowClass}">
            <td class="property-name">${escapeHtml(label)}</td>
            <td class="${cellClass1}">${displayValue1}</td>
            <td class="${cellClass2}">${displayValue2}</td>
          </tr>
        `;
      });

      comparisonHTML += `</tbody></table></div>`;
    });
  });

  const statsHTML = `
    <div class="stats">
      <div class="stat-card"><div class="stat-number">${totalFeatures}</div><div class="stat-label">Total Features</div></div>
      <div class="stat-card"><div class="stat-number">${sameFeatures}</div><div class="stat-label">Same</div></div>
      <div class="stat-card"><div class="stat-number">${differentFeatures}</div><div class="stat-label">Different</div></div>
      <div class="stat-card"><div class="stat-number">${missingInProduct1}</div><div class="stat-label">Only in ${escapeHtml(name2)}</div></div>
      <div class="stat-card"><div class="stat-number">${missingInProduct2}</div><div class="stat-label">Only in ${escapeHtml(name1)}</div></div>
    </div>
  `;

  return {
    statsHTML,
    comparisonHTML,
    stats: { total: totalFeatures, same: sameFeatures, different: differentFeatures, missingInProduct1, missingInProduct2 }
  };
}

// ─────────────────────────────────────────────────────────────────────────────
// buildComparisonTable  (main full-page comparison renderer)
// ─────────────────────────────────────────────────────────────────────────────

function buildComparisonTable(...products) {
  if (products.length < 2) return '<p>Musisz wybrać co najmniej 2 produkty do porównania.</p>';
  if (products.length > 4) return '<p>Możesz porównać maksymalnie 4 produkty.</p>';

  window.comparisonProducts = products;

  // Parse specification_table for all products
  const specs = products.map(product => {
    if (!product || !product.specification_table) return null;
    return typeof product.specification_table === 'string'
      ? JSON.parse(product.specification_table)
      : product.specification_table;
  });

  if (specs.some(spec => !spec)) {
    return '<p>Błąd: nie wszystkie produkty mają dane specyfikacji.</p>';
  }

  const productCount = products.length;
  const featureColumnWidth = productCount === 2 ? 35 : 30;
  const productColumnWidth = (100 - featureColumnWidth) / productCount;

  const shortNames = products.map(p =>
    p.title.length > 20 ? p.title.substring(0, 20) + '...' : p.title
  );
  const mobileShortNames = products.map(p =>
    p.title.length > 12 ? p.title.substring(0, 12) + '...' : p.title
  );

  let sectionIndex = 0;

  // ── HTML: top controls + sticky header + desktop table head ───────────────
  let html = `
    <div class="top-action-controls desktop-only" style="display:flex;justify-content:flex-end;gap:12px;margin-bottom:20px;padding:16px 0;">
      <a href="/collections/all" class="browse-products-btn-top" id="smart-collection-link-top" style="display:inline-flex;align-items:center;gap:8px;padding:12px 20px;background:white;border:2px solid #000;border-radius:6px;font-size:14px;font-weight:600;cursor:pointer;transition:all 0.3s ease;color:#000;text-decoration:none;">
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><rect x="3" y="3" width="7" height="7"></rect><rect x="14" y="3" width="7" height="7"></rect><rect x="14" y="14" width="7" height="7"></rect><rect x="3" y="14" width="7" height="7"></rect></svg>
        <span>Przeglądaj produkty</span>
      </a>
      <button id="share-comparison-top" class="share-comparison-btn-top" style="display:inline-flex;align-items:center;gap:8px;padding:12px 20px;background:white;border:2px solid #000;border-radius:6px;font-size:14px;font-weight:600;cursor:pointer;transition:all 0.3s ease;color:#000;">
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="18" cy="5" r="3"></circle><circle cx="6" cy="12" r="3"></circle><circle cx="18" cy="19" r="3"></circle><line x1="8.59" y1="13.51" x2="15.42" y2="17.49"></line><line x1="15.41" y1="6.51" x2="8.59" y2="10.49"></line></svg>
        <span>Udostępnij porównanie</span>
      </button>
    </div>

    <div class="comparison-wrapper">
      <!-- Sticky Header Desktop -->
      <div class="sticky-header desktop-only">
        <div class="sticky-header-row">
          <div class="sticky-feature-column" style="width:${featureColumnWidth}%">
            <div class="comparison-controls">
              <div class="toggle-differences-switch">
                <label class="switch-label">
                  <span class="switch-label-text">Tylko różnice</span>
                  <div class="switch-toggle">
                    <input type="checkbox" id="toggle-differences-sticky">
                    <span class="switch-slider"></span>
                  </div>
                </label>
              </div>
              <button id="expand-all-sections-sticky" class="expand-all-btn">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polyline points="6 9 12 15 18 9"></polyline></svg>
                <span>Rozwiń wszystko</span>
              </button>
            </div>
          </div>
          ${products.map((product, index) => `
            <div class="sticky-product-column" style="width:${productColumnWidth}%">
              <a href="${escapeHtml(product.url)}">
                <img src="${escapeHtml(product.featured_image)}" alt="${escapeHtml(product.title)}" class="sticky-product-image">
                <div class="sticky-product-details">
                  <span class="sticky-product-name">${escapeHtml(shortNames[index])}</span>
                  <div class="sticky-product-price">
                    ${product.compare_at_price && product.compare_at_price > product.price
                      ? `<span class="sticky-compare-price">${formatPrice(product.compare_at_price)}</span><span class="sticky-current-price text-on-sale">${formatPrice(product.price)}</span>`
                      : `<span class="sticky-current-price">${formatPrice(product.price)}</span>`}
                  </div>
                </div>
              </a>
            </div>
          `).join('')}
        </div>
      </div>

      <!-- Desktop Table -->
      <table class="comparison-table desktop-only">
        <thead>
          <tr class="product-header">
            <th class="feature-column" style="width:${featureColumnWidth}%">
              <div class="comparison-controls">
                <div class="toggle-differences-switch">
                  <label class="switch-label">
                    <span class="switch-label-text">Pokaż tylko różnice</span>
                    <div class="switch-toggle">
                      <input type="checkbox" id="toggle-differences">
                      <span class="switch-slider"></span>
                    </div>
                  </label>
                </div>
                <button id="expand-all-sections" class="expand-all-btn">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polyline points="6 9 12 15 18 9"></polyline></svg>
                  <span>Rozwiń wszystko</span>
                </button>
              </div>
            </th>
            ${products.map((product, index) => `
              <th class="product-column" style="width:${productColumnWidth}%">
                <div class="product-info">
                  <a href="${product.url}">
                    <img src="${escapeHtml(product.featured_image)}" alt="${escapeHtml(product.title)}" class="product-image">
                  </a>
                  <h3 class="product-name"><a href="${product.url}">${escapeHtml(product.title)}</a></h3>
                  <div class="product-price">
                    ${product.compare_at_price && product.compare_at_price > product.price
                      ? `<span class="compare-price">${formatPrice(product.compare_at_price)}</span><span class="current-price text-on-sale">${formatPrice(product.price)}</span>`
                      : `<span class="current-price">${formatPrice(product.price)}</span>`}
                  </div>
                  ${product.available ? addToCartFromComparison(product.id) : '<span class="out-of-stock">Niedostępny</span>'}
                </div>
              </th>
            `).join('')}
          </tr>
        </thead>
        <tbody>
  `;

  // ── Desktop table rows ─────────────────────────────────────────────────────
  SECTION_CONFIG.forEach(({ keyA, keyB }) => {
    // Collect all unique section titles across all products for this config entry
    const allSectionTitles = new Set();
    specs.forEach(spec => {
      resolveSection(spec, keyA, keyB).forEach(s => allSectionTitles.add(s.title));
    });

    if (allSectionTitles.size === 0) return;

    Array.from(allSectionTitles).sort().forEach(sectionTitle => {
      // Rows per product for this section
      const allRows = specs.map(spec => {
        const sections = resolveSection(spec, keyA, keyB);
        const found = sections.find(s => s.title === sectionTitle);
        return found ? found.rows : [];
      });

      const allLabels = new Set();
      allRows.forEach(rows => rows.forEach(r => allLabels.add(r.label)));
      if (allLabels.size === 0) return;

      let sectionDiff = false;
      const currentSectionId = sectionIndex++;
      const sortedLabels = Array.from(allLabels).sort();

      let tmpHtml = '';
      sortedLabels.forEach(label => {
        const values = allRows.map(rows => {
          const row = rows.find(r => r.label === label);
          return row ? row.value : null;
        });

        let rowClass = 'comparison-row';
        const allNull = values.every(v => v === null);
        const allSame = values.every((v, i, arr) =>
          i === 0 || normalizeValue(v) === normalizeValue(arr[0])
        );

        if (allNull) {
          rowClass += ' both-missing';
        } else if (allSame) {
          rowClass += ' same';
        } else {
          rowClass += ' difference';
          sectionDiff = true;
        }

        tmpHtml += `
          <tr class="${rowClass}" ${currentSectionId === 0 ? '' : 'style="display:none;"'}>
            <td class="feature-name">${escapeHtml(label)}</td>
            ${values.map(value => {
              const display = value === null ? '<span class="value-missing">-</span>' : formatValue(value);
              return `<td class="feature-value">${display}</td>`;
            }).join('')}
          </tr>
        `;
      });

      html += `
        <tr class="section-header ${sectionDiff ? 'difference' : 'same'} ${currentSectionId === 0 ? '' : 'collapsed'}">
          <td colspan="${productCount + 1}">
            ${escapeHtml(sectionTitle)}
            <svg class="section-accordion-icon" width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
              <path d="M8 4l4 6H4l4-6z"/>
            </svg>
          </td>
        </tr>
      `;
      html += tmpHtml;
    });
  });

  html += `
        </tbody>
      </table>

      <!-- Mobile Version -->
      <div class="comparison-mobile mobile-only">
        <div class="mobile-sticky-controls">
          <div class="comparison-controls">
            <div class="comparison-controls-row">
              <a href="/collections/all" class="browse-products-btn" id="smart-collection-link-mobile">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><rect x="3" y="3" width="7" height="7"></rect><rect x="14" y="3" width="7" height="7"></rect><rect x="14" y="14" width="7" height="7"></rect><rect x="3" y="14" width="7" height="7"></rect></svg>
                <span>Przeglądaj produkty</span>
              </a>
              <button id="share-comparison-mobile" class="share-comparison-btn">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="18" cy="5" r="3"></circle><circle cx="6" cy="12" r="3"></circle><circle cx="18" cy="19" r="3"></circle><line x1="8.59" y1="13.51" x2="15.42" y2="17.49"></line><line x1="15.41" y1="6.51" x2="8.59" y2="10.49"></line></svg>
                <span>Udostępnij porównanie</span>
              </button>
              <button id="expand-all-sections-mobile" class="expand-all-btn">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polyline points="6 9 12 15 18 9"></polyline></svg>
                <span>Rozwiń</span>
              </button>
              <div class="toggle-differences-switch">
                <label class="switch-label">
                  <span class="switch-label-text">Tylko różnice</span>
                  <div class="switch-toggle">
                    <input type="checkbox" id="toggle-differences-mobile">
                    <span class="switch-slider"></span>
                  </div>
                </label>
              </div>
            </div>
          </div>
        </div>

        <div class="mobile-product-headers" style="position:sticky;top:94px;z-index:90;background:white;box-shadow:0 2px 4px rgba(0,0,0,0.1);">
          ${products.map((product, index) => `
            <div class="mobile-product-header">
              <a href="${escapeHtml(product.url)}">
                <img src="${escapeHtml(product.featured_image)}" alt="${escapeHtml(product.title)}" class="mobile-product-image">
              </a>
              <h3 class="mobile-product-name"><a href="${escapeHtml(product.url)}">${escapeHtml(product.title)}</a></h3>
            </div>
          `).join('')}
        </div>
  `;

  // ── Mobile rows ────────────────────────────────────────────────────────────
  sectionIndex = 0;

  SECTION_CONFIG.forEach(({ keyA, keyB }) => {
    const allSectionTitles = new Set();
    specs.forEach(spec => {
      resolveSection(spec, keyA, keyB).forEach(s => allSectionTitles.add(s.title));
    });

    if (allSectionTitles.size === 0) return;

    Array.from(allSectionTitles).sort().forEach(sectionTitle => {
      const allRows = specs.map(spec => {
        const sections = resolveSection(spec, keyA, keyB);
        const found = sections.find(s => s.title === sectionTitle);
        return found ? found.rows : [];
      });

      const allLabels = new Set();
      allRows.forEach(rows => rows.forEach(r => allLabels.add(r.label)));
      if (allLabels.size === 0) return;

      const currentMobileSectionId = sectionIndex++;

      html += `
        <div class="mobile-section-header ${currentMobileSectionId === 0 ? '' : 'collapsed'}">
          ${escapeHtml(sectionTitle)}
          <svg class="section-accordion-icon" width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
            <path d="M8 4l4 6H4l4-6z"/>
          </svg>
        </div>
        <div class="mobile-comparison-table" ${currentMobileSectionId === 0 ? '' : 'style="display:none;"'}>
      `;

      Array.from(allLabels).sort().forEach(label => {
        const values = allRows.map(rows => {
          const row = rows.find(r => r.label === label);
          return row ? row.value : null;
        });

        let rowClass = 'mobile-comparison-row';
        const allNull = values.every(v => v === null);
        const allSame = values.every((v, i, arr) =>
          i === 0 || normalizeValue(v) === normalizeValue(arr[0])
        );

        if (allNull)       rowClass += ' both-missing';
        else if (allSame)  rowClass += ' same';
        else               rowClass += ' difference';

        html += `
          <div class="${rowClass}">
            <div class="mobile-feature-name">${escapeHtml(label)}</div>
            <div class="mobile-values">
              ${values.map((value, index) => {
                const display = value === null ? '<span class="value-missing">-</span>' : formatValue(value);
                return `
                  <div class="mobile-value">
                    <span class="mobile-product-label">${escapeHtml(mobileShortNames[index])}</span>
                    ${display}
                  </div>
                `;
              }).join('')}
            </div>
          </div>
        `;
      });

      html += `</div>`; // close mobile-comparison-table
    });
  });

  html += `</div></div>`; // close comparison-mobile + comparison-wrapper

  return html;
}

// ─────────────────────────────────────────────────────────────────────────────
// ADD TO CART HELPER
// ─────────────────────────────────────────────────────────────────────────────

function addToCartFromComparison(productIndex) {
  return `<form method="post" action="/cart/add" id="product-form-${productIndex}-template--22655556387124__main" accept-charset="UTF-8" class="shopify-product-form" enctype="multipart/form-data" is="product-form">
    <input type="hidden" name="form_type" value="product">
    <input type="hidden" name="utf8" value="✓">
    <input type="hidden" name="id" value="${productIndex}">
    <div class="v-stack gap-4">
      <buy-buttons class="buy-buttons" template="szablon-produktowy-4" form="product-form-${productIndex}-template--22655556387124__main">
        <button type="submit" class="button button--md" style="--button-background:204 255 0/var(--button-background-opacity,1);--button-outline-color:240 196 23;--button-text-color:26 26 26;" is="custom-button">
          <div>Dodaj do koszyka</div>
          <span class="button__loader"><span></span><span></span><span></span></span>
        </button>
      </buy-buttons>
    </div>
    <input type="hidden" name="product-id" value="${productIndex}">
    <input type="hidden" name="section-id" value="template--22655556387124__main">
  </form>`;
}

// ─────────────────────────────────────────────────────────────────────────────
// UI INITIALISERS  (unchanged from original — no structural dependency)
// ─────────────────────────────────────────────────────────────────────────────

function initStickyHeader() {
  const wrapper = document.querySelector('.comparison-wrapper');
  const stickyHeaderDesktop = document.querySelector('.sticky-header.desktop-only');
  const announcementBar = document.querySelector('.shopify-section--announcement-bar')?.offsetHeight ?? 0;
  const productHeader = document.querySelector('.product-header');
  const shopifyHeader = document.querySelector('.shopify-section--header');

  if (!wrapper) return;

  function getHeaderHeight() {
    return shopifyHeader ? (shopifyHeader.offsetHeight + announcementBar) : (window.innerWidth <= 768 ? 50 : 100);
  }

  function updateStickyPosition() {
    if (stickyHeaderDesktop) stickyHeaderDesktop.style.top = getHeaderHeight() + 'px';
  }

  updateStickyPosition();

  let resizeTimer;
  window.addEventListener('resize', function() {
    clearTimeout(resizeTimer);
    resizeTimer = setTimeout(function() { updateStickyPosition(); handleScroll(); }, 250);
  });

  const handleScroll = () => {
    const headerHeight = getHeaderHeight();
    const isMobile = window.innerWidth <= 768;

    if (isMobile) {
      if (stickyHeaderDesktop) stickyHeaderDesktop.classList.remove('visible');
    } else {
      const headerRect = productHeader ? productHeader.getBoundingClientRect() : null;
      if (headerRect && headerRect.bottom <= headerHeight) {
        if (stickyHeaderDesktop) stickyHeaderDesktop.classList.add('visible');
      } else {
        if (stickyHeaderDesktop) stickyHeaderDesktop.classList.remove('visible');
      }
    }
  };

  window.addEventListener('scroll', handleScroll);
  wrapper.addEventListener('scroll', handleScroll);
  handleScroll();
}

function initScrollHint() {
  const wrapper = document.querySelector('.comparison-wrapper');
  if (!wrapper) return;

  function checkScroll() {
    wrapper.classList.toggle('has-scroll', wrapper.scrollWidth > wrapper.clientWidth);
  }

  checkScroll();
  window.addEventListener('resize', checkScroll);
  wrapper.addEventListener('scroll', function() { wrapper.classList.remove('has-scroll'); }, { once: true });
}

function initMobileValueTooltips() {
  if (window.innerWidth > 768) return;

  const mobileValues = document.querySelectorAll('.mobile-value');
  let tooltipTimeout;
  let currentTooltip = null;

  const fullProductNames = Array.from(document.querySelectorAll('.mobile-product-header')).map(h => {
    const el = h.querySelector('.mobile-product-name');
    return el ? el.textContent : '';
  });

  mobileValues.forEach(valueEl => {
    valueEl.addEventListener('click', function(e) {
      e.stopPropagation();
      const valuesContainer = this.parentElement;
      const allValuesInRow = Array.from(valuesContainer.querySelectorAll('.mobile-value'));
      const valueIndex = allValuesInRow.indexOf(this);
      const productName = fullProductNames[valueIndex] || 'Produkt';

      if (currentTooltip) currentTooltip.remove();

      const tooltip = document.createElement('div');
      tooltip.className = 'mobile-value-tooltip';
      tooltip.textContent = productName;
      document.body.appendChild(tooltip);
      currentTooltip = tooltip;

      setTimeout(() => tooltip.classList.add('show'), 10);
      clearTimeout(tooltipTimeout);

      tooltipTimeout = setTimeout(() => {
        tooltip.classList.remove('show');
        setTimeout(() => { tooltip.remove(); if (currentTooltip === tooltip) currentTooltip = null; }, 300);
      }, 2000);
    });
  });

  window.addEventListener('scroll', function() {
    if (currentTooltip) {
      currentTooltip.classList.remove('show');
      setTimeout(() => { if (currentTooltip) { currentTooltip.remove(); currentTooltip = null; } }, 300);
    }
  }, { passive: true });
}

function initDifferencesToggle() {
  const toggleInputs = [
    document.getElementById('toggle-differences'),
    document.getElementById('toggle-differences-sticky'),
    document.getElementById('toggle-differences-mobile')
  ].filter(Boolean);

  if (toggleInputs.length === 0) return;

  let showOnlyDifferences = false;

  toggleInputs.forEach(input => {
    input.addEventListener('change', function() {
      showOnlyDifferences = this.checked;
      toggleInputs.forEach(o => { o.checked = showOnlyDifferences; });

      document.querySelectorAll('.comparison-row, .mobile-comparison-row').forEach(row => {
        if (row.classList.contains('section-header')) return;
        if (showOnlyDifferences && !row.classList.contains('difference')) {
          row.classList.add('hidden');
        } else {
          row.classList.remove('hidden');
        }
      });

      handleEmptySections(showOnlyDifferences);
    });
  });
}

function handleEmptySections(showOnlyDifferences) {
  if (!showOnlyDifferences) {
    document.querySelectorAll('.section-header, .mobile-section-header').forEach(h => h.classList.remove('hidden'));
    return;
  }

  document.querySelectorAll('.section-header').forEach(header => {
    let next = header.nextElementSibling;
    let hasVisible = false;
    while (next && !next.classList.contains('section-header')) {
      if (!next.classList.contains('hidden')) { hasVisible = true; break; }
      next = next.nextElementSibling;
    }
    header.classList.toggle('hidden', !hasVisible);
  });

  document.querySelectorAll('.mobile-section-header').forEach(header => {
    const table = header.nextElementSibling;
    if (!table || !table.classList.contains('mobile-comparison-table')) return;
    const hasVisible = Array.from(table.querySelectorAll('.mobile-comparison-row')).some(r => !r.classList.contains('hidden'));
    header.classList.toggle('hidden', !hasVisible);
    table.classList.toggle('hidden', !hasVisible);
  });
}

function initComparisonAccordions() {
  const desktopHeaders = document.querySelectorAll('.section-header');
  const mobileHeaders  = document.querySelectorAll('.mobile-section-header');

  desktopHeaders.forEach(header => {
    header.addEventListener('click', function(e) {
      e.preventDefault();
      e.stopPropagation();

      const isCollapsed = this.classList.contains('collapsed');
      this.classList.toggle('collapsed');

      let next = this.nextElementSibling;
      while (next && !next.classList.contains('section-header')) {
        if (next.classList.contains('comparison-row')) {
          next.style.display = isCollapsed ? '' : 'none';
        }
        next = next.nextElementSibling;
      }
    });
  });

  mobileHeaders.forEach(header => {
    header.addEventListener('click', function(e) {
      e.preventDefault();
      e.stopPropagation();

      const isCollapsed = this.classList.contains('collapsed');
      this.classList.toggle('collapsed');

      const table = this.nextElementSibling;
      if (table && table.classList.contains('mobile-comparison-table')) {
        table.style.display = isCollapsed ? '' : 'none';
      }
    });
  });

  const expandAllBtns = [
    document.getElementById('expand-all-sections'),
    document.getElementById('expand-all-sections-sticky'),
    document.getElementById('expand-all-sections-mobile')
  ].filter(Boolean);

  expandAllBtns.forEach(btn => {
    btn.addEventListener('click', function(e) {
      e.preventDefault();

      const allExpanded = !this.classList.contains('all-expanded');

      expandAllBtns.forEach(b => {
        if (allExpanded) {
          b.querySelector('span').textContent = b.id.includes('mobile') ? 'Zwiń' : 'Zwiń wszystko';
          b.classList.add('all-expanded');
        } else {
          b.querySelector('span').textContent = b.id.includes('mobile') ? 'Rozwiń' : 'Rozwiń wszystko';
          b.classList.remove('all-expanded');
        }
      });

      desktopHeaders.forEach(header => {
        header.classList.toggle('collapsed', !allExpanded);
        let next = header.nextElementSibling;
        while (next && !next.classList.contains('section-header')) {
          if (next.classList.contains('comparison-row')) next.style.display = allExpanded ? '' : 'none';
          next = next.nextElementSibling;
        }
      });

      mobileHeaders.forEach(header => {
        header.classList.toggle('collapsed', !allExpanded);
        const table = header.nextElementSibling;
        if (table && table.classList.contains('mobile-comparison-table')) {
          table.style.display = allExpanded ? '' : 'none';
        }
      });
    });
  });
}

function initAddMoreProducts() {
  const addBtn = document.getElementById('add-more-products');
  if (!addBtn) return;

  const urlParams = new URLSearchParams(window.location.search);
  const currentHandles = [];
  for (let i = 1; i <= 4; i++) {
    const handle = urlParams.get(`handle${i}`);
    if (handle && handle !== 'undefined') currentHandles.push(handle);
  }

  if (currentHandles.length >= 4) {
    addBtn.disabled = true;
    addBtn.innerHTML = `<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><line x1="12" y1="5" x2="12" y2="19"></line><line x1="5" y1="12" x2="19" y2="12"></line></svg><span>Maksimum (4)</span>`;
    return;
  }

  addBtn.innerHTML = `<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><line x1="12" y1="5" x2="12" y2="19"></line><line x1="5" y1="12" x2="19" y2="12"></line></svg><span>Dodaj produkt (${currentHandles.length}/4)</span>`;
  addBtn.addEventListener('click', function() { openProductSelector(currentHandles); });
}

function openProductSelector(currentHandles) {
  const modal = document.createElement('div');
  modal.className = 'product-selector-modal';
  modal.innerHTML = `
    <div class="product-selector-content">
      <div class="product-selector-header">
        <h3>Wybierz produkt do porównania</h3>
        <button class="product-selector-close">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>
        </button>
      </div>
      <div class="product-selector-search">
        <input type="text" placeholder="Szukaj produktu..." id="product-search-input">
      </div>
      <div class="product-selector-body">
        <div class="product-selector-loading">Ładowanie produktów...</div>
      </div>
    </div>
  `;

  document.body.appendChild(modal);
  setTimeout(() => modal.classList.add('active'), 10);

  modal.querySelector('.product-selector-close').addEventListener('click', () => {
    modal.classList.remove('active');
    setTimeout(() => modal.remove(), 300);
  });

  modal.addEventListener('click', e => {
    if (e.target === modal) { modal.classList.remove('active'); setTimeout(() => modal.remove(), 300); }
  });

  loadAvailableProducts(modal, currentHandles);
}

async function loadAvailableProducts(modal, currentHandles) {
  const body = modal.querySelector('.product-selector-body');

  if (!window.CompareManager || !window.CompareManager.specCache) {
    body.innerHTML = '<div class="product-selector-empty"><p>CompareManager nie jest załadowany.</p></div>';
    return;
  }

  const cachedProducts = Object.keys(window.CompareManager.specCache || {})
    .filter(handle => window.CompareManager.specCache[handle]?.hasSpec === true)
    .filter(handle => !currentHandles.includes(handle));

  if (cachedProducts.length === 0) {
    body.innerHTML = '<div class="product-selector-empty"><p>Brak dostępnych produktów do porównania.</p><p style="font-size:0.9em;color:#999;margin-top:8px;">Produkty z specyfikacją pojawią się tutaj po przeglądaniu strony.</p></div>';
    return;
  }

  const products = await Promise.all(cachedProducts.map(handle => window.fetchTechnicalSpecification(handle)));
  renderProductList(body, products.filter(Boolean), currentHandles);
}

function renderProductList(container, products, currentHandles) {
  if (products.length === 0) {
    container.innerHTML = '<div class="product-selector-empty">Nie znaleziono produktów</div>';
    return;
  }

  container.innerHTML = `
    <div class="product-selector-list">
      ${products.map(product => {
        const isAdded = currentHandles.includes(product.handle);
        return `
          <div class="product-selector-item ${isAdded ? 'disabled' : ''}" data-handle="${product.handle}">
            <img src="${product.featured_image}" alt="${product.title}" class="product-selector-item-image">
            <div class="product-selector-item-info">
              <div class="product-selector-item-title">${escapeHtml(product.title)}</div>
              <div class="product-selector-item-status">${isAdded ? 'Już w porównaniu' : 'Kliknij aby dodać'}</div>
            </div>
          </div>
        `;
      }).join('')}
    </div>
  `;

  container.querySelectorAll('.product-selector-item:not(.disabled)').forEach(item => {
    item.addEventListener('click', function() { addProductToComparison(this.dataset.handle, currentHandles); });
  });

  const searchInput = document.getElementById('product-search-input');
  if (searchInput) {
    searchInput.addEventListener('input', function() {
      const q = this.value.toLowerCase();
      container.querySelectorAll('.product-selector-item').forEach(item => {
        const title = item.querySelector('.product-selector-item-title').textContent.toLowerCase();
        item.style.display = title.includes(q) ? '' : 'none';
      });
    });
  }
}

function addProductToComparison(handle, currentHandles) {
  const isComparisonPage = window.location.pathname.includes('/pages/porownaj-produkty') ||
                           window.location.pathname.includes('/pages/compare-products');

  if (isComparisonPage) {
    const searchResults = document.getElementById('comparison-search-results');
    if (searchResults) searchResults.innerHTML = '<div class="no-results">Dodawanie produktu...</div>';

    const newHandles = [...currentHandles, handle];
    const params = newHandles.filter(Boolean).map((h, i) => `handle${i + 1}=${encodeURIComponent(h)}`).join('&');
    window.location.href = `/pages/porownaj-produkty?${params}`;
  } else {
    if (window.CompareManager) {
      window.fetchTechnicalSpecification(handle).then(product => {
        if (!product) return;
        window.CompareManager.specCache[handle] = { hasSpec: true, lastAccess: Date.now() };
        window.CompareManager.initializedHandles.add(handle);
        window.CompareManager.saveCache();

        const added = window.CompareManager.addProduct({
          handle: product.handle,
          title: product.title,
          image: product.featured_image
        });

        if (added) {
          const searchResults = document.getElementById('comparison-search-results');
          if (searchResults) searchResults.style.display = 'none';
          const searchInput = document.querySelector('#comparison-search-form input[name="q"]');
          if (searchInput) searchInput.value = '';
        }
      }).catch(err => {
        console.error('Error adding product:', err);
        if (window.CompareManager) window.CompareManager.showNotification('Nie udało się dodać produktu', 'error');
      });
    }
  }
}

function initComparisonSearch() {
  const searchForm = document.getElementById('comparison-search-form');
  const searchInput = searchForm?.querySelector('input[name="q"]');
  const resultsContainer = document.getElementById('comparison-search-results');

  if (!searchForm || !searchInput) return;

  let searchTimeout;

  searchInput.addEventListener('input', function() {
    clearTimeout(searchTimeout);
    const query = this.value.trim();
    if (query.length < 2) { resultsContainer.style.display = 'none'; return; }
    searchTimeout = setTimeout(() => performSearch(query), 300);
  });

  searchForm.addEventListener('submit', function(e) {
    e.preventDefault();
    const query = searchInput.value.trim();
    if (query.length >= 2) performSearch(query);
  });

  async function performSearch(query) {
    try {
      resultsContainer.innerHTML = '<div class="no-results">Szukam...</div>';
      resultsContainer.style.display = 'block';

      const response = await fetch(`/search/suggest.json?q=${encodeURIComponent(query)}&resources[type]=product&resources[limit]=10`);
      const data = await response.json();
      displaySearchResults(data.resources.results.products || []);
    } catch (error) {
      console.error('Search error:', error);
      resultsContainer.innerHTML = '<div class="no-results">Błąd wyszukiwania</div>';
    }
  }

  function displaySearchResults(products) {
    if (products.length === 0) {
      resultsContainer.innerHTML = '<div class="no-results">Nie znaleziono produktów</div>';
      resultsContainer.style.display = 'block';
      return;
    }

    const urlParams = new URLSearchParams(window.location.search);
    const currentHandles = [];
    for (let i = 1; i <= 4; i++) {
      const handle = urlParams.get(`handle${i}`);
      if (handle && handle !== 'undefined') currentHandles.push(handle);
    }

    const resultsHTML = products.map(product => {
      const isAdded = currentHandles.includes(product.handle);
      const price = product.price / 100;

      let imageUrl = product.featured_image || product.image || (product.images?.[0]) || '';
      if (imageUrl && typeof imageUrl === 'object') imageUrl = imageUrl.src || imageUrl.url || '';
      imageUrl = String(imageUrl || '');
      if (imageUrl && !imageUrl.startsWith('http')) imageUrl = 'https:' + imageUrl;

      return `
        <div class="search-result-item ${isAdded ? 'disabled' : ''}" data-handle="${product.handle}">
          ${imageUrl ? `<img src="${imageUrl}" alt="${escapeHtml(product.title)}" class="search-result-image">` : '<div class="search-result-image search-result-no-image"></div>'}
          <div class="search-result-info">
            <div class="search-result-title">${escapeHtml(product.title)}</div>
            <div class="search-result-price">${price.toFixed(2)} zł</div>
          </div>
          <button class="search-result-add" ${isAdded ? 'disabled' : ''}>${isAdded ? 'W porównaniu' : 'Dodaj'}</button>
        </div>
      `;
    }).join('');

    resultsContainer.innerHTML = `<div class="search-results-list">${resultsHTML}</div>`;
    resultsContainer.style.display = 'block';

    resultsContainer.querySelectorAll('.search-result-item:not(.disabled)').forEach(item => {
      item.addEventListener('click', function(e) {
        if (e.target.classList.contains('search-result-add')) return;
        addProductToComparison(this.dataset.handle, currentHandles);
      });
      const addBtn = item.querySelector('.search-result-add');
      if (addBtn && !addBtn.disabled) {
        addBtn.addEventListener('click', e => { e.stopPropagation(); addProductToComparison(item.dataset.handle, currentHandles); });
      }
    });
  }

  document.addEventListener('click', function(e) {
    if (!searchForm.contains(e.target) && !resultsContainer.contains(e.target)) {
      resultsContainer.style.display = 'none';
    }
  });
}

function initSmartCollectionLink(products) {
  const link = document.getElementById('smart-collection-link');
  if (!link || !products?.length) return;
  link.href = '/collections/all';
}

function initShareComparison() {
  const shareBtns = [document.getElementById('share-comparison-top')].filter(Boolean);

  shareBtns.forEach(shareBtn => {
    shareBtn.addEventListener('click', function() {
      const currentURL = window.location.href;

      const modal = document.createElement('div');
      modal.className = 'share-modal-overlay';
      modal.innerHTML = `
        <div class="share-modal">
          <div class="share-modal-header">
            <h3>Udostępnij porównanie</h3>
            <button class="share-modal-close">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>
            </button>
          </div>
          <div class="share-url-container">
            <input type="text" class="share-url-input" value="${escapeHtml(currentURL)}" readonly>
            <button class="share-copy-btn">Kopiuj</button>
          </div>
          <p style="font-size:0.9em;color:#666;margin-top:12px;">Skopiuj ten link i udostępnij go.</p>
        </div>
      `;

      document.body.appendChild(modal);
      setTimeout(() => modal.classList.add('active'), 10);

      modal.querySelector('.share-modal-close').addEventListener('click', () => {
        modal.classList.remove('active'); setTimeout(() => modal.remove(), 300);
      });
      modal.addEventListener('click', e => {
        if (e.target === modal) { modal.classList.remove('active'); setTimeout(() => modal.remove(), 300); }
      });

      const copyBtn = modal.querySelector('.share-copy-btn');
      const urlInput = modal.querySelector('.share-url-input');
      copyBtn.addEventListener('click', () => {
        // Use modern clipboard API with fallback
        if (navigator.clipboard && navigator.clipboard.writeText) {
          navigator.clipboard.writeText(currentURL).then(() => {
            copyBtn.textContent = 'Skopiowano!';
            copyBtn.classList.add('copied');
            setTimeout(() => { copyBtn.textContent = 'Kopiuj'; copyBtn.classList.remove('copied'); }, 2000);
          }).catch(() => fallbackCopy());
        } else {
          fallbackCopy();
        }

        function fallbackCopy() {
          urlInput.select();
          urlInput.setSelectionRange(0, 99999); // mobile
          document.execCommand('copy');
          copyBtn.textContent = 'Skopiowano!';
          copyBtn.classList.add('copied');
          setTimeout(() => { copyBtn.textContent = 'Kopiuj'; copyBtn.classList.remove('copied'); }, 2000);
        }
      });
    });
  });
}

// ─────────────────────────────────────────────────────────────────────────────
// GLOBAL EXPORTS
// ─────────────────────────────────────────────────────────────────────────────

if (typeof window !== 'undefined') {
  window.fetchTechnicalSpecification  = fetchTechnicalSpecification;
  window.buildComparisonTable         = buildComparisonTable;
  window.createCompleteComparison     = createCompleteComparison;
  window.initStickyHeader             = initStickyHeader;
  window.initScrollHint               = initScrollHint;
  window.initMobileValueTooltips      = initMobileValueTooltips;
  window.initDifferencesToggle        = initDifferencesToggle;
  window.initAddMoreProducts          = initAddMoreProducts;
  window.initComparisonAccordions     = initComparisonAccordions;
  window.initComparisonSearch         = initComparisonSearch;
  window.initSmartCollectionLink      = initSmartCollectionLink;
  window.initShareComparison          = initShareComparison;
  window.normalizeValue               = normalizeValue;
  window.formatValue                  = formatValue;
  window.escapeHtml                   = escapeHtml;
  window.formatPrice                  = formatPrice;
  window.replaceAndParseJSON          = replaceAndParseJSON;
  // Note: generateShortURL removed — share uses window.location.href directly

  console.log('Comparison functions loaded and available globally');
}